import { useEffect } from 'react';
import { useI18nStore } from '../stores/i18n-store';
import { useI18nLanguagesStore, UiLanguage } from '../stores/i18n-languages-store';
import { Button } from '@/core/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/core/components/ui/dropdown-menu';
import { Globe, Check, Loader2, AlertCircle } from 'lucide-react';
import { ApplicationType } from '@/features/translations/types/translation-types';

/**
 * Flag emoji mapping for common language codes
 * Falls back to globe icon for unknown codes
 */
const FLAG_EMOJIS: Record<string, string> = {
  en: '🇺🇸',
  'en-us': '🇺🇸',
  'en-gb': '🇬🇧',
  'en-au': '🇦🇺',
  he: '🇮🇱',
  ar: '🇸🇦',
  es: '🇪🇸',
  fr: '🇫🇷',
  de: '🇩🇪',
  it: '🇮🇹',
  pt: '🇵🇹',
  nl: '🇳🇱',
  pl: '🇵🇱',
  ru: '🇷🇺',
  zh: '🇨🇳',
  ja: '🇯🇵',
  ko: '🇰🇷',
};

/**
 * Get flag emoji for a language code
 */
function getFlag(code: string): string {
  const normalizedCode = code.toLowerCase();
  return FLAG_EMOJIS[normalizedCode] || FLAG_EMOJIS[normalizedCode.split('-')[0]] || '🌐';
}

interface LanguageSwitcherProps {
  /** Show only the flag on mobile */
  compact?: boolean;
  /** Button variant */
  variant?: 'outline' | 'ghost' | 'default';
  /** Button size */
  size?: 'sm' | 'default' | 'lg';
  /** Additional class names */
  className?: string;
}

/**
 * Language Switcher Component
 * Dropdown menu for switching between available languages
 * Fetches languages dynamically from the translation API
 */
export function LanguageSwitcher({
  compact = false,
  variant = 'outline',
  size = 'sm',
  className = '',
}: LanguageSwitcherProps) {
  const { currentLanguage, changeLanguage, isLoading: isChangingLanguage, applicationType } = useI18nStore();
  const {
    languages,
    isLoading: isLoadingLanguages,
    error,
    fetchLanguages,
    hasFetched
  } = useI18nLanguagesStore();

  // Fetch languages on mount if not already fetched
  useEffect(() => {
    if (!hasFetched && !isLoadingLanguages) {
      fetchLanguages(applicationType);
    }
  }, [hasFetched, isLoadingLanguages, fetchLanguages, applicationType]);

  // Find current language in the list
  const normalizedCurrentLang = currentLanguage.toLowerCase();
  const currentLang = languages.find(
    (l) => l.code.toLowerCase() === normalizedCurrentLang
  ) || languages.find(
    (l) => l.code.toLowerCase().startsWith(normalizedCurrentLang.split('-')[0])
  );

  const isLoading = isChangingLanguage || isLoadingLanguages;
  const hasLanguages = languages.length > 0;

  // Handle language change
  const handleLanguageChange = async (lang: UiLanguage) => {
    await changeLanguage(lang.code);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          disabled={isLoading}
          className={`min-w-[48px] ${className}`}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : error ? (
            <AlertCircle className="h-4 w-4 text-destructive" />
          ) : (
            <>
              <Globe className="h-4 w-4" />
              {!compact && (
                <span className="ml-2 hidden sm:inline">
                  {currentLang?.nativeName || currentLanguage}
                </span>
              )}
              {!compact && (
                <span className="ml-2 sm:hidden">{getFlag(currentLanguage)}</span>
              )}
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[180px] max-h-[400px] overflow-y-auto">
        {error && (
          <DropdownMenuItem
            onClick={() => fetchLanguages(applicationType)}
            className="text-destructive cursor-pointer"
          >
            <AlertCircle className="h-4 w-4 mr-2" />
            <span>Failed to load. Click to retry.</span>
          </DropdownMenuItem>
        )}
        {!hasLanguages && !error && isLoadingLanguages && (
          <DropdownMenuItem disabled>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            <span>Loading languages...</span>
          </DropdownMenuItem>
        )}
        {!hasLanguages && !error && !isLoadingLanguages && (
          <DropdownMenuItem disabled>
            <span className="text-muted-foreground">No languages available</span>
          </DropdownMenuItem>
        )}
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang)}
            className="flex items-center justify-between cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <span>{getFlag(lang.code)}</span>
              <span>{lang.nativeName}</span>
              {lang.name !== lang.nativeName && (
                <span className="text-muted-foreground text-xs">({lang.name})</span>
              )}
            </span>
            {currentLanguage.toLowerCase() === lang.code.toLowerCase() && (
              <Check className="h-4 w-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
